import type { Localize, ProfileAssessmentOptions } from "../../types/assessment";

export function getProfileAssessmentOptions(L: Localize): ProfileAssessmentOptions {
  return {
    ageRange: [
      ["under_18", L("Under 18", "Unter 18")],
      ["18_29", "18-29"],
      ["30_44", "30-44"],
      ["45_64", "45-64"],
      ["65_plus", "65+"],
    ],
    householdSize: [
      ["1_person", L("1 person", "1 Person")],
      ["2_people", L("2 people", "2 Personen")],
      ["3_4_people", L("3-4 people", "3-4 Personen")],
      ["5_or_more_people", L("5 or more", "5 oder mehr")],
    ],
    livingSituation: [
      ["alone", L("Alone", "Allein")],
      ["with_partner", L("With partner", "Mit Partner/in")],
      ["with_family", L("With family", "Mit Familie")],
      ["shared_housing", L("Shared housing", "Wohngemeinschaft")],
      ["assisted_living", L("Assisted living", "Betreutes Wohnen")],
      ["other", L("Other", "Andere")],
    ],
    programScope: [
      ["me_only", L("Me only", "Nur ich")],
      ["me_and_my_household", L("Me and my household", "Ich und mein Haushalt")],
      ["me_and_dependents", L("Me and dependents", "Ich und abhängige Personen")],
    ],
    generalHealth: [
      ["very_good", L("Very good", "Sehr gut")],
      ["good", L("Good", "Gut")],
      ["fair", L("Fair", "Mittel")],
      ["limited", L("Limited", "Eingeschränkt")],
      ["severely_limited", L("Severely limited", "Stark eingeschränkt")],
    ],
    medicationReserve: [
      ["no_prescription_dependency", L("No prescription dependency", "Keine regelmäßige Medikation")],
      ["no_special_storage", L("Medication, no special storage", "Medikamente ohne besondere Lagerung")],
      ["requires_refrigeration_or_special_storage", L("Requires refrigeration/special storage", "Kühlung/Sonderlagerung erforderlich")],
      ["fewer_than_14_days_reserve", L("Fewer than 14 days reserve", "Weniger als 14 Tage Vorrat")],
      ["14_29_days_reserve", L("14-29 days reserve", "14-29 Tage Vorrat")],
      ["30_plus_days_reserve", L("30+ days reserve", "30+ Tage Vorrat")],
    ],
    mobility: [
      ["unrestricted", L("Unrestricted", "Uneingeschränkt")],
      ["slight_limitation", L("Slight limitation", "Leichte Einschränkung")],
      ["significant_limitation", L("Significant limitation", "Deutliche Einschränkung")],
      ["dependent_on_aids", L("Dependent on aids", "Auf Hilfsmittel angewiesen")],
      ["dependent_on_another_person", L("Dependent on another person", "Auf andere Person angewiesen")],
    ],
    pressure: [
      ["very_stable", L("Very stable", "Sehr stabil")],
      ["rather_stable", L("Rather stable", "Eher stabil")],
      ["variable", L("Variable", "Wechselhaft")],
      ["often_overwhelmed", L("Often overwhelmed", "Oft überfordert")],
      ["strongly_burdened", L("Strongly burdened", "Stark belastet")],
    ],
    housingType: [
      ["single_family_house", L("Single-family house", "Einfamilienhaus")],
      ["multi_family_house", L("Multi-family house", "Mehrfamilienhaus")],
      ["high_rise", L("High-rise", "Hochhaus")],
      ["assisted_living", L("Assisted living", "Betreutes Wohnen")],
      ["other", L("Other", "Andere")],
    ],
    children: [
      ["none", L("None", "Keine")],
      ["0_5_years", "0-5"],
      ["6_12_years", "6-12"],
      ["13_17_years", "13-17"],
    ],
    careCount: [
      ["0", "0"],
      ["1", "1"],
      ["2", "2"],
      ["3_plus", "3+"],
    ],
    careIntensity: [
      ["none", L("None", "Keine")],
      ["mild", L("Mild", "Leicht")],
      ["moderate", L("Moderate", "Mittel")],
      ["heavy", L("Heavy", "Hoch")],
    ],
    disability: [
      ["no", L("No", "Nein")],
      ["yes", L("Yes", "Ja")],
      ["prefer_not_to_say", L("Prefer not to say", "Keine Angabe")],
    ],
    contacts: [
      ["0", "0"],
      ["1", "1"],
      ["2", "2"],
      ["3_plus", "3+"],
    ],
    neighbourhood: [
      ["limited", L("Limited", "Begrenzt")],
      ["some", L("Some", "Etwas")],
      ["good", L("Good", "Gut")],
    ],
    likelyHelp: [
      ["none", L("None", "Keine")],
      ["information", L("Information", "Information")],
      ["check_in", L("Check-in", "Nachfragen")],
      ["shopping", L("Shopping", "Einkaufen")],
      ["transport", L("Transport", "Transport")],
    ],
    transportModes: [
      ["walking_or_rolling_route", L("Walking/rolling route", "Fuß-/Rollweg")],
      ["bicycle", L("Bicycle", "Fahrrad")],
      ["car", L("Car", "Auto")],
      ["public_transport", L("Public transport", "ÖPNV")],
      ["reliable_contact", L("Reliable contact", "Verlässlicher Kontakt")],
      ["taxi_or_ride_service", L("Taxi / ride service", "Taxi/Fahrdienst")],
      ["none", L("None", "Keine")],
    ],
    barrierFree: [
      ["no", L("No", "Nein")],
      ["partially", L("Partially", "Teilweise")],
      ["yes", L("Yes, fully", "Ja, vollständig")],
    ],
    docsCopies: [
      ["paper_and_digital", L("Paper and digital", "Papier und digital")],
      ["paper_only", L("Paper only", "Nur Papier")],
      ["digital_only", L("Digital only", "Nur digital")],
      ["none", L("None", "Keine")],
    ],
    emergencyDocs: [
      ["emergency_contacts", L("Emergency contacts", "Notfallkontakte")],
      ["medication_list", L("Medication list", "Medikamentenliste")],
      ["insurance", L("Insurance", "Versicherung")],
      ["patient_provision", L("Patient provision", "Patientenverfügung")],
      ["power_of_attorney", L("Power of attorney", "Vollmacht")],
      ["none", L("None", "Keine")],
    ],
    skills: [
      ["first_aid", L("First aid", "Erste Hilfe")],
      ["cooking_without_power", L("Cooking without power", "Kochen ohne Strom")],
      ["repairs", L("Repairs", "Reparaturen")],
      ["planning", L("Planning", "Planung")],
      ["orientation_and_navigation", L("Orientation/navigation", "Orientierung/Navigation")],
      ["gardening", L("Gardening", "Gartenarbeit")],
      ["none", L("None", "Keine")],
    ],
    risks: [
      ["power_outage", L("Power outage", "Stromausfall")],
      ["heat", L("Heat", "Hitze")],
      ["water_disruption", L("Water disruption", "Wasserausfall")],
      ["flood", L("Flood", "Hochwasser")],
      ["storm", L("Storm", "Sturm")],
      ["snow_or_ice", L("Snow / ice", "Schnee/Eis")],
      ["supply_disruption", L("Supply disruption", "Versorgungsstörung")],
      ["civil_unrest", L("Civil unrest", "Unruhen")],
    ],
  };
}
